import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Stack,
  Skeleton,
} from "@mui/material";
import { tooltipStyleProps } from "services/constants/Styles";
import { CalendarToday, Person, RestartAlt } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { format } from "date-fns";
import {
  Add,
  People,
  Mail,
  Phone,
  Business,
  Label,
  MoreVert,
  Edit,
  Delete,
  Upload,
  ViewModule,
  TableChart,
} from "@mui/icons-material";
import { Menu } from "@mui/material";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useFetch } from "services/hooks/useFetch";
import ContactDetails from "components/contacts/ContactDetails";
import CloseIcon from "@mui/icons-material/Close";
import ContactImport from "components/contacts/ContactImport";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function Contacts() {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [viewMode, setViewMode] = useState("card"); // card or table view
  const [sortConfig, setSortConfig] = useState({
    key: "first_name",
    direction: "asc",
  });
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [showImport, setShowImport] = useState(false);
  const [sortValue, setSortValue] = useState("first_name_asc");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [tempFilters, setTempFilters] = useState({
    status: "all",
    source: "all",
    agent: userDetails?.id || "all",
    date: null,
    location: "all",
  });
  const [locationFilter, setLocationFilter] = useState("all");
  const globalSearch = useSelector(
    (state) => state.tokenReducer.globalSearchValue || ""
  );

  const history = useHistory();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    contact: null,
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuContactId, setMenuContactId] = useState(null);
  const { result: users } = useFetch(
    `${domain}crm/product-based/user/?product=CRM`
  );
  const [agentFilter, setAgentFilter] = useState(userDetails?.id || "all");
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [dateFilter, setDateFilter] = useState(null);
  const location = useLocation();

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    // 🔑 Keep dropdown in sync
    setSortValue(`${key}_${direction}`);
  };

  const filterContacts = React.useCallback(() => {
    let filtered = [...contacts];
    if (globalSearch) {
      const term = globalSearch.toLowerCase();
      filtered = filtered.filter((c) => {
        const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
        const company = c.company?.toLowerCase() || "";
        const emails = c.emails?.some((e) =>
          e.email?.toLowerCase().includes(term)
        );
        const phones = c.phone_numbers?.some((p) =>
          p.number?.toLowerCase().includes(term)
        );
        const cities = Array.isArray(c.addresses)
          ? c.addresses.some((a) => a.city?.toLowerCase().includes(term))
          : false;
        const agentName = c.username?.toLowerCase() || ""; // comes from API when mapping

        return (
          fullName.includes(term) ||
          company.includes(term) ||
          emails ||
          phones ||
          cities ||
          agentName.includes(term)
        );
      });
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          `${c.first_name} ${c.last_name}`.toLowerCase().includes(term) ||
          c.company?.toLowerCase().includes(term) ||
          c.emails?.some((e) => e.email?.toLowerCase().includes(term))
      );
    }
    if (statusFilter !== "all")
      filtered = filtered.filter((c) => c.status === statusFilter);
    if (sourceFilter !== "all")
      filtered = filtered.filter((c) => c.source === sourceFilter);
    if (agentFilter !== "all")
      filtered = filtered.filter((c) => c.assigned_to === agentFilter);
    if (dateFilter) {
      const selectedDate = new Date(dateFilter);
      selectedDate.setHours(0, 0, 0, 0);

      filtered = filtered.filter((c) => {
        if (!c.createdat) return false;
        const contactDate = new Date(c.createdat);
        contactDate.setHours(0, 0, 0, 0);
        return contactDate.getTime() === selectedDate.getTime();
      });
    }
    const norm = (s) => (s ?? "").toString().trim().toLowerCase();
    if (locationFilter !== "all") {
      const target = norm(locationFilter);
      filtered = filtered.filter((c) =>
        Array.isArray(c.addresses)
          ? c.addresses.some((a) => norm(a.city) === target)
          : false
      );
    }

    setFilteredContacts(filtered);
  }, [
    contacts,
    globalSearch,
    searchTerm,
    statusFilter,
    sourceFilter,
    agentFilter,
    dateFilter,
    locationFilter,
  ]);

  const locationOptions = React.useMemo(() => {
    const norm = (s) => (s ?? "").toString().trim().toLowerCase();
    const map = new Map(); // key: normalized, value: first-seen original
    contacts.forEach((c) => {
      if (Array.isArray(c.addresses)) {
        c.addresses.forEach((a) => {
          const key = norm(a?.city);
          if (key && !map.has(key)) map.set(key, a.city);
        });
      }
    });
    return Array.from(map.values());
  }, [contacts]);

  const sortContacts = (contacts, { key, direction }) => {
    const order = direction === "asc" ? 1 : -1;

    return [...contacts].sort((a, b) => {
      switch (key) {
        case "company":
          return (a.company || "").localeCompare(b.company || "") * order;
        case "first_name":
          return (
            `${a.first_name || ""} ${a.last_name || ""}`
              .trim()
              .toLowerCase()
              .localeCompare(
                `${b.first_name || ""} ${b.last_name || ""}`
                  .trim()
                  .toLowerCase()
              ) * order
          );
        case "email":
          return (
            (getPrimaryEmail(a) || "").localeCompare(getPrimaryEmail(b) || "") *
            order
          );
        case "phone":
          return (
            (getPrimaryPhone(a) || "").localeCompare(getPrimaryPhone(b) || "") *
            order
          );
        case "agent":
          return (a.username || "").localeCompare(b.username || "") * order;
        case "status":
          return (
            (a.status_name || "").localeCompare(b.status_name || "") * order
          );
        case "createdat":
          return (new Date(a.createdat) - new Date(b.createdat)) * order;
        default:
          return 0;
      }
    });
  };

  const sortedContacts = sortContacts(filteredContacts, sortConfig);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function getPrimaryEmail(contact) {
    return (
      contact.emails?.find((e) => e.primary)?.email ||
      contact.emails?.[0]?.email
    );
  }

  function getPrimaryPhone(contact) {
    return (
      contact.phone_numbers?.find((p) => p.primary)?.number ||
      contact.phone_numbers?.[0]?.number
    );
  }

  useEffect(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (sourceFilter !== "all") count++;
    if (agentFilter !== "all") count++;
    if (dateFilter) count++;
    if (locationFilter !== "all") count++;
    setFilterCount(count);
  }, [statusFilter, sourceFilter, agentFilter, dateFilter, locationFilter]);

  const loadContacts = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const agentParam = agentFilter !== "all" ? `?agent=${agentFilter}` : "";
      const response = await customGetAPI(
        `${domain}crm/contacts/${agentParam}`
      );
      const data = response.responseDetails;
      setContacts(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error("Failed to load contacts", error);
      setContacts([]);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [domain, agentFilter]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    if (location.state?.contactId && contacts.length > 0) {
      const found = contacts.find((c) => c.id === location.state.contactId);
      if (found) {
        setSelectedContact(found);
      }
    }
  }, [location.state, contacts]);

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-status/`);

        setStatusOptions(res.responseDetails || []);
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    const fetchSourceOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-source/`);
        setSourceOptions(res.responseDetails || []);
      } catch (err) {
        console.error("Failed to fetch contact sources:", err);
      }
    };

    fetchStatusOptions();
    fetchSourceOptions();
  }, [domain]);

  useEffect(() => {
    filterContacts();
  }, [
    contacts,
    searchTerm,
    statusFilter,
    sourceFilter,
    agentFilter,
    dateFilter,
    locationFilter,
    globalSearch,
    filterContacts,
  ]);

  const handleDeleteContact = async () => {
    console.log("Deleting contact:", deleteModal.contact);
    if (!deleteModal.contact) return;
    const payload = {
      isdeleted: true,
    };
    try {
      const response = await customAPI(
        `${domain}crm/contacts/${deleteModal.contact.id}/`,
        "PATCH",
        payload
      );
      console.log("Delete response:", response);
      if (response.code === 200) {
        toast.success("Contact deleted successfully");
        setContacts((prev) =>
          prev.filter((c) => c.id !== deleteModal.contact.id)
        );

        // Clear selection if viewing deleted contact
        if (selectedContact?.id === deleteModal.contact.id) {
          setSelectedContact(null);
        }
      }
      if (selectedContact?.id === deleteModal.contact.id)
        setSelectedContact(null);
      setDeleteModal({ isOpen: false, contact: null });
    } catch (err) {
      console.error("Error deleting contact", err);
    }
  };

  const openMenu = (event, contactId) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuContactId(contactId);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuContactId(null);
  };

  const stringToColor = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }
    return color;
  };

  // if (isLoading) {
  //   return (
  //     <Box
  //       display="flex"
  //       justifyContent="center"
  //       alignItems="center"
  //       height="50vh"
  //     >
  //       <CircularProgress />
  //     </Box>
  //   );
  // }

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        overflowY: "auto",
      },
    },
  };
  const mappedContacts = sortedContacts.map((contact) => {
    const agentUser = users?.find((u) => u.id === contact.assigned_to);
    return {
      ...contact,
      agent_image_url: agentUser?.image_url || null,
    };
  });

  const handleOpenDrawer = () => {
    setTempFilters({
      status: statusFilter,
      source: sourceFilter,
      agent: agentFilter,
      date: dateFilter,
      location: locationFilter,
    });
    setDrawerOpen(true);
  };

  const handleApplyFilters = () => {
    setStatusFilter(tempFilters.status);
    setSourceFilter(tempFilters.source);
    setAgentFilter(tempFilters.agent);
    setDateFilter(tempFilters.date);
    setLocationFilter(tempFilters.location || "all");
    setDrawerOpen(false);
  };

  // Reset filters in drawer
  const handleResetTempFilters = () => {
    setTempFilters({
      status: "all",
      source: "all",
      agent: userDetails?.id || "all",
      date: null,
      location: "all",
    });
    handleResetAllFilters();
    setDrawerOpen(false);
  };

  // Reset all filters
  const handleResetAllFilters = () => {
    setStatusFilter("all");
    setSourceFilter("all");
    setAgentFilter(userDetails?.id || "all");
    setDateFilter(null);
    setLocationFilter("all");
    setSearchTerm("");
  };

  const sortData = (data, sortConfig) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const valA = (a[sortConfig.key] || "").toString().toLowerCase();
      const valB = (b[sortConfig.key] || "").toString().toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(mappedContacts, sortConfig);

  return (
    <Box p={4}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={selectedContact ? 8 : 12}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: 1,
            }}
          >
            {/* Left Title */}
            <Box flex={1}>
              <Typography variant="h4">Contacts</Typography>
            </Box>

            {/* Right Actions */}
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                overflowX: { xs: "auto", sm: "visible" }, // allow scroll on small screens
                "&::-webkit-scrollbar": { display: "none" }, // hide scrollbar
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setShowImport(true)}
                sx={{
                  backgroundColor: "#84DEB8",
                  color: "#000",
                  "&:hover": { backgroundColor: "#6cc9a1" },
                }}
                startIcon={<Upload />}
              >
                Import
              </Button>
              <Button
                variant="contained"
                onClick={() => history.push("/admin/crm/create")}
                startIcon={<Add />}
                sx={{
                  backgroundColor: "#84DEB8",
                  color: "#000",
                  "&:hover": { backgroundColor: "#6cc9a1" },
                }}
              >
                Add Contact
              </Button>
              <IconButton onClick={handleOpenDrawer} title="Filters">
                <Tooltip slotProps={tooltipStyleProps} title={"Filters"} arrow>
                  <Badge badgeContent={filterCount} color="primary">
                    <FilterListIcon />
                  </Badge>
                </Tooltip>
              </IconButton>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    const lastUnderscore = value.lastIndexOf("_");
                    const key = value.substring(0, lastUnderscore);
                    const direction = value.substring(lastUnderscore + 1);
                    setSortValue(value);
                    setSortConfig({ key, direction });
                  }}
                  label="Sort By"
                  MenuProps={menuProps}
                >
                  <MenuItem value="company_asc">Company (A–Z)</MenuItem>
                  <MenuItem value="company_desc">Company (Z–A)</MenuItem>
                  <MenuItem value="first_name_asc">First Name (A–Z)</MenuItem>
                  <MenuItem value="first_name_desc">First Name (Z–A)</MenuItem>
                  <MenuItem value="email_asc">Email (A–Z)</MenuItem>
                  <MenuItem value="email_desc">Email (Z–A)</MenuItem>
                  <MenuItem value="phone_asc">Phone (A–Z)</MenuItem>
                  <MenuItem value="phone_desc">Phone (Z–A)</MenuItem>
                  <MenuItem value="agent_asc">Agent (A–Z)</MenuItem>
                  <MenuItem value="agent_desc">Agent (Z–A)</MenuItem>
                  <MenuItem value="status_asc">Status (A–Z)</MenuItem>
                  <MenuItem value="status_desc">Status (Z–A)</MenuItem>
                  <MenuItem value="createdat_desc">Created (Newest)</MenuItem>
                  <MenuItem value="createdat_asc">Created (Oldest)</MenuItem>
                </Select>
              </FormControl>
              <Mui.ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) setViewMode(newMode);
                }}
                size="small"
              >
                <Tooltip arrow slotProps={tooltipStyleProps} title="Card View">
                  <Mui.ToggleButton value="card">
                    <ViewModule fontSize="small" />
                  </Mui.ToggleButton>
                </Tooltip>
                <Tooltip arrow slotProps={tooltipStyleProps} title="Table View">
                  <Mui.ToggleButton value="table">
                    <TableChart fontSize="small" />
                  </Mui.ToggleButton>
                </Tooltip>
              </Mui.ToggleButtonGroup>
              <Tooltip
                arrow
                slotProps={tooltipStyleProps}
                title="Reset Filters"
              >
                <IconButton onClick={handleResetAllFilters}>
                  <RestartAlt />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Grid container spacing={2} mb={2}>
            {/* Filter Drawer */}
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              ModalProps={{
                BackdropProps: {
                  onClick: (e) => e.stopPropagation(),
                  style: { background: "transparent" },
                },
                disableEscapeKeyDown: true,
              }}
            >
              <Box sx={{ width: 350, padding: 2 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <Typography variant="h6">Advanced Filters</Typography>
                  <IconButton onClick={() => setDrawerOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Status Filter */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    MenuProps={menuProps}
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "12px",
                      },
                    }}
                    value={tempFilters.status}
                    onChange={(e) =>
                      setTempFilters({ ...tempFilters, status: e.target.value })
                    }
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Source Filter */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Source</InputLabel>
                  <Select
                    MenuProps={menuProps}
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "12px",
                      },
                    }}
                    value={tempFilters.source}
                    onChange={(e) =>
                      setTempFilters({ ...tempFilters, source: e.target.value })
                    }
                    label="Source"
                  >
                    <MenuItem value="all">All</MenuItem>
                    {sourceOptions.map((source) => (
                      <MenuItem key={source.id} value={source.id}>
                        {source.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Agent Filter */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Agent</InputLabel>
                  <Select
                    MenuProps={menuProps}
                    value={tempFilters.agent}
                    onChange={(e) =>
                      setTempFilters({ ...tempFilters, agent: e.target.value })
                    }
                    label="Agent"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "12px",
                      },
                    }}
                    renderValue={(selected) => {
                      if (selected === "all") return "All Agents";

                      // if selected = logged-in user
                      if (selected === userDetails?.id) {
                        return (
                          <Box display="flex" alignItems="center" gap={1}>
                            {userDetails?.image_url ? (
                              <Avatar
                                src={userDetails.image_url}
                                sx={{ width: 24, height: 24 }}
                              />
                            ) : (
                              <Avatar sx={{ width: 24, height: 24 }}>
                                {userDetails?.username?.[0]?.toUpperCase()}
                              </Avatar>
                            )}
                            <Typography>{userDetails?.username}</Typography>
                          </Box>
                        );
                      }

                      const selectedUser = users?.find(
                        (u) => u.id === selected
                      );
                      return (
                        <Box display="flex" alignItems="center" gap={1}>
                          {selectedUser?.image_url ? (
                            <Avatar
                              src={selectedUser.image_url}
                              sx={{ width: 24, height: 24 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {selectedUser?.username?.[0]?.toUpperCase()}
                            </Avatar>
                          )}
                          <Typography>
                            {selectedUser?.username || "Unknown"}
                          </Typography>
                        </Box>
                      );
                    }}
                  >
                    {/* Logged-in user option */}
                    {userDetails && (
                      <MenuItem value={userDetails.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {userDetails.image_url ? (
                            <Avatar
                              src={userDetails.image_url}
                              sx={{ width: 24, height: 24 }}
                            />
                          ) : (
                            <Avatar sx={{ width: 24, height: 24 }}>
                              {userDetails.username?.[0]?.toUpperCase()}
                            </Avatar>
                          )}
                          <Typography>Me ({userDetails.username})</Typography>
                        </Box>
                      </MenuItem>
                    )}

                    {/* All agents */}
                    <MenuItem value="all">All Agents</MenuItem>

                    {/* Other users */}
                    {users
                      ?.filter((u) => u.id !== userDetails?.id) // 👈 avoid duplicate
                      .map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            {user.image_url ? (
                              <Avatar
                                src={user.image_url}
                                sx={{ width: 24, height: 24 }}
                              />
                            ) : (
                              <Avatar sx={{ width: 24, height: 24 }}>
                                {user.username?.[0]?.toUpperCase()}
                              </Avatar>
                            )}
                            <Typography>{user.username}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {/* Date Filter */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={enGB}
                  >
                    <DatePicker
                      label="Date"
                      value={tempFilters.date}
                      onChange={(newDate) =>
                        setTempFilters({ ...tempFilters, date: newDate })
                      }
                      renderInput={(params) => (
                        <TextField fullWidth {...params} />
                      )}
                      inputFormat="dd-MM-yyyy"
                      sx={{
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "12px",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </FormControl>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>City</InputLabel>
                  <Select
                    MenuProps={menuProps}
                    value={tempFilters.location ?? "all"}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        location: e.target.value,
                      })
                    }
                    label="Location"
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "12px",
                      },
                    }}
                    renderValue={(val) => (val === "all" ? "All" : val)} // optional UX nicety
                  >
                    <MenuItem value="all">All</MenuItem>
                    {locationOptions.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Action Buttons */}
                <Box display="flex" gap={1} mt={3}>
                  <Button
                    variant="outlined"
                    onClick={handleResetTempFilters}
                    fullWidth
                    startIcon={<RefreshIcon />}
                  >
                    Reset
                  </Button>
                  <Button
                    sx={{
                      backgroundColor: "#84DEB8",
                      color: "#000",
                      "&:hover": { backgroundColor: "#6cc9a1" },
                    }}
                    variant="contained"
                    fullWidth
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Drawer>
          </Grid>
          <Box mt={4} />
          {viewMode === "card" ? (
            <Grid container spacing={2}>
              {isLoading ? (
                Array.from(new Array(rowsPerPage)).map((_, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Skeleton variant="circular" width={44} height={44} />
                          <Box flex={1}>
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="60%" />
                          </Box>
                        </Box>
                        <Box mt={2} display="flex" gap={1}>
                          <Skeleton
                            variant="rectangular"
                            width={60}
                            height={24}
                          />
                          <Skeleton
                            variant="rectangular"
                            width={80}
                            height={24}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : filteredContacts.length > 0 ? (
                sortedData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((contact) => (
                    <Grid item xs={12} key={contact.id}>
                      <Card
                        onClick={() => setSelectedContact(contact)}
                        sx={{
                          cursor: "pointer",
                          border:
                            selectedContact?.id === contact.id
                              ? "2px solid #1976d2"
                              : "1px solid #e0e0e0",
                          borderRadius: 2,
                          boxShadow:
                            selectedContact?.id === contact.id
                              ? "0 4px 16px rgba(25,118,210,0.15)"
                              : "0 2px 8px rgba(0,0,0,0.06)",
                          transition: "0.2s ease",
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            height: "100%",
                            width: "4px",
                            backgroundColor:
                              selectedContact?.id === contact.id
                                ? "#1976d2"
                                : "transparent",
                            borderTopLeftRadius: "inherit",
                            borderBottomLeftRadius: "inherit",
                          },
                        }}
                      >
                        <CardContent sx={{ px: 3, py: 2 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar
                                sx={{
                                  bgcolor: stringToColor(
                                    contact.company
                                      ? contact.company
                                      : `${contact.first_name} ${contact.last_name}`
                                  ),
                                  width: 44,
                                  height: 44,
                                  fontWeight: 600,
                                }}
                              >
                                {contact.company
                                  ? contact.company[0]?.toUpperCase()
                                  : `${
                                      contact.first_name?.[0]?.toUpperCase() ||
                                      ""
                                    }${
                                      contact.last_name?.[0]?.toUpperCase() ||
                                      ""
                                    }`}
                              </Avatar>
                              <Box>
                                <Tooltip
                                  slotProps={tooltipStyleProps}
                                  title={
                                    contact.company
                                      ? "Company Name"
                                      : "Contact Name"
                                  }
                                  arrow
                                >
                                  <Typography fontWeight={600} fontSize="1rem">
                                    {contact.company
                                      ? contact.company
                                      : `${contact.first_name} ${contact.last_name}`}
                                  </Typography>
                                </Tooltip>
                                <Box
                                  display="flex"
                                  gap={2}
                                  fontSize={13}
                                  color="text.secondary"
                                  flexWrap="wrap"
                                >
                                  {getPrimaryEmail(contact) && (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}
                                    >
                                      <Mail fontSize="small" />{" "}
                                      {getPrimaryEmail(contact)}
                                    </Box>
                                  )}
                                  {getPrimaryPhone(contact) && (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}
                                    >
                                      <Phone fontSize="small" />{" "}
                                      {getPrimaryPhone(contact)}
                                    </Box>
                                  )}
                                  {contact.company && (
                                    <Tooltip
                                      arrow
                                      slotProps={tooltipStyleProps}
                                      title="Contact Name"
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                      >
                                        <Business fontSize="small" />{" "}
                                        {contact.first_name}
                                      </Box>
                                    </Tooltip>
                                  )}
                                  {contact.username && (
                                    <Tooltip
                                      arrow
                                      slotProps={tooltipStyleProps}
                                      title="Agent"
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                      >
                                        {/* <Person fontSize="small" />{" "} */}
                                        {contact.agent_image_url ? (
                                          <Avatar
                                            src={contact.agent_image_url}
                                            sx={{
                                              width: 20,
                                              height: 20,
                                              minWidth: 20,
                                            }}
                                          />
                                        ) : (
                                          <Person fontSize="small" />
                                        )}
                                        {contact.username}
                                      </Box>
                                    </Tooltip>
                                  )}
                                  {contact.createdat && (
                                    <Tooltip
                                      arrow
                                      slotProps={tooltipStyleProps}
                                      title="Cerated At"
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                      >
                                        <CalendarToday fontSize="small" />
                                        {format(
                                          new Date(contact.createdat),
                                          "MMM d, yyyy h:mm a"
                                        )}
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={contact.status_name}
                                sx={{
                                  backgroundColor: contact.status_color,
                                  color: "#fff",
                                }}
                                size="small"
                              />
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMenu(e, contact.id);
                                }}
                                aria-label="contact actions"
                              >
                                <MoreVert />
                              </IconButton>
                            </Box>
                          </Box>
                          {contact.tags?.length > 0 && (
                            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                              {contact.tags.slice(0, 3).map((tag, i) => (
                                <Chip
                                  key={i}
                                  size="small"
                                  icon={<Label fontSize="small" />}
                                  label={tag}
                                />
                              ))}
                              {contact.tags.length > 3 && (
                                <Chip
                                  size="small"
                                  label={`+${contact.tags.length - 3} more`}
                                />
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                      <Menu
                        anchorEl={menuAnchorEl}
                        open={menuContactId === contact.id}
                        onClose={closeMenu}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MenuItem
                          onClick={() => {
                            history.push(`/admin/crm/edit/${contact.id}`);
                            closeMenu();
                          }}
                        >
                          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setDeleteModal({ isOpen: true, contact });
                            closeMenu();
                          }}
                          sx={{ color: "red" }}
                        >
                          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                        </MenuItem>
                      </Menu>
                    </Grid>
                  ))
              ) : hasLoadedOnce && contacts.length > 0 ? (
                <Grid item xs={12} textAlign="center" py={4}>
                  <People fontSize="large" sx={{ color: "gray" }} />
                  <Typography variant="h6">No contacts found</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ||
                    statusFilter !== "all" ||
                    sourceFilter !== "all" ||
                    agentFilter !== "all" ||
                    dateFilter ||
                    locationFilter !== "all"
                      ? "Try adjusting your search or filters"
                      : "Get started by adding your first contact"}
                  </Typography>
                </Grid>
              ) : (
                !isLoading &&
                hasLoadedOnce && (
                  /* 4. Truly Empty System */ <Grid
                    item
                    xs={12}
                    textAlign="center"
                    py={4}
                  >
                    <People fontSize="large" sx={{ color: "gray" }} />
                    <Typography variant="h6">No contacts available</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get started by adding your first contact
                    </Typography>
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        onClick={() => history.push("/admin/crm/create")}
                        startIcon={<Add />}
                      >
                        Add Contact
                      </Button>
                    </Box>
                  </Grid>
                )
              )}
            </Grid>
          ) : (
            <ContactsTable
              data={sortedData}
              page={page}
              rowsPerPage={rowsPerPage}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleSort={handleSort}
              sortConfig={sortConfig}
              setSelectedContact={setSelectedContact}
              openMenu={openMenu}
              menuAnchorEl={menuAnchorEl}
              menuContactId={menuContactId}
              closeMenu={closeMenu}
              getPrimaryEmail={getPrimaryEmail}
              getPrimaryPhone={getPrimaryPhone}
              history={history}
              setDeleteModal={setDeleteModal}
              statusFilter={statusFilter}
              sourceFilter={sourceFilter}
              agentFilter={agentFilter}
              dateFilter={dateFilter}
              searchTerm={searchTerm}
            />
          )}

          {viewMode === "card" && filteredContacts.length > rowsPerPage && (
            <Mui.TablePagination
              className="custom-pagination"
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={filteredContacts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                mt: 3,
                ".MuiTablePagination-toolbar": {
                  justifyContent: "flex-end",
                },
              }}
            />
          )}
        </Grid>

        {selectedContact && (
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              position: "sticky",
              top: 16,
              alignSelf: "flex-start",
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
            }}
          >
            <ContactDetails
              contact={selectedContact}
              onClose={() => setSelectedContact(null)}
              onUpdateContact={(updatedContact) => {
                if (updatedContact && updatedContact.id) {
                  setContacts((prev) =>
                    prev.map((c) =>
                      c.id === updatedContact.id ? updatedContact : c
                    )
                  );
                  setSelectedContact(updatedContact);
                }
              }}
            />
          </Grid>
        )}
      </Grid>

      {showImport && (
        <ContactImport
          open={showImport}
          onClose={() => setShowImport(false)}
          onImportSuccess={() => {
            setShowImport(false);
            loadContacts();
          }}
          users={users}
        />
      )}

      <DeleteConfirmModal
        open={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, contact: null })}
        onConfirm={handleDeleteContact}
        contactName={
          deleteModal.contact
            ? `${deleteModal.contact.first_name} ${deleteModal.contact.last_name}`
            : ""
        }
      />
    </Box>
  );
}

const DeleteConfirmModal = ({ open, onClose, onConfirm, contactName }) => {
  const handleClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      onClose();
    }
  };
  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Delete Contact
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete <strong>{contactName}</strong>? This
          action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContactsTable = ({
  data,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSort,
  sortConfig,
  setSelectedContact,
  openMenu,
  menuAnchorEl,
  menuContactId,
  closeMenu,
  getPrimaryEmail,
  getPrimaryPhone,
  history,
  setDeleteModal,
  statusFilter,
  sourceFilter,
  agentFilter,
  dateFilter,
  searchTerm,
}) => {
  const columns = [
    { key: "company", label: "Company" },
    { key: "first_name", label: "First Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "agent", label: "Agent" },
    { key: "status", label: "Status" },
    { key: "createdat", label: "Created At" },
    { key: "actions", label: "Actions" },
  ];

  return (
    <Mui.TableContainer
      component={Mui.Paper}
      sx={{ borderRadius: 2, boxShadow: 2 }}
    >
      <Mui.Table>
        <Mui.TableHead>
          <Mui.TableRow>
            {columns.map((col) => (
              <Mui.TableCell
                key={col.key}
                onClick={() => col.key !== "actions" && handleSort(col.key)}
                sx={{
                  fontWeight: 600,
                  cursor: col.key !== "actions" ? "pointer" : "default",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                {col.label}
                {sortConfig.key === col.key && (
                  <Mui.Box component="span" sx={{ ml: 0.5 }}>
                    {sortConfig.direction === "asc" ? "↑" : "↓"}
                  </Mui.Box>
                )}
              </Mui.TableCell>
            ))}
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {data.length === 0 ? (
            <Mui.TableRow>
              <Mui.TableCell colSpan={8} align="center">
                <People fontSize="large" sx={{ color: "gray" }} />
                <Typography variant="h6">No contacts found</Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ||
                  statusFilter !== "all" ||
                  sourceFilter !== "all" ||
                  agentFilter !== "all" ||
                  dateFilter
                    ? "No data available for the selected filter(s)"
                    : "Get started by adding your first contact"}
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => history.push("/admin/crm/create")}
                    startIcon={<Add />}
                  >
                    Add Contact
                  </Button>
                </Box>
              </Mui.TableCell>
            </Mui.TableRow>
          ) : (
            data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((c) => (
                <Mui.TableRow
                  hover
                  key={c.id}
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedContact(c)}
                >
                  {/* Company */}
                  <Mui.TableCell>{c.company || "-"}</Mui.TableCell>
                  {/* First Name */}
                  <Mui.TableCell>{c.first_name || "-"}</Mui.TableCell>
                  {/* Email */}
                  <Mui.TableCell>{getPrimaryEmail(c) || "-"}</Mui.TableCell>
                  {/* Phone */}
                  <Mui.TableCell>{getPrimaryPhone(c) || "-"}</Mui.TableCell>
                  {/* Agent with avatar */}
                  <Mui.TableCell>
                    <Mui.Box display="flex" alignItems="center" gap={1}>
                      {c.agent_image_url ? (
                        <Mui.Avatar
                          src={c.agent_image_url}
                          sx={{ width: 24, height: 24 }}
                        />
                      ) : (
                        <Mui.Avatar sx={{ width: 24, height: 24 }}>
                          {c.username?.[0]?.toUpperCase()}
                        </Mui.Avatar>
                      )}
                      {c.username || "-"}
                    </Mui.Box>
                  </Mui.TableCell>
                  {/* Status */}
                  <Mui.TableCell>
                    <Mui.Chip
                      label={c.status_name}
                      size="small"
                      sx={{ backgroundColor: c.status_color, color: "#fff" }}
                    />
                  </Mui.TableCell>
                  {/* Created At */}
                  <Mui.TableCell>
                    {c.createdat
                      ? format(new Date(c.createdat), "MMM d, yyyy h:mm a")
                      : "-"}
                  </Mui.TableCell>
                  {/* Actions */}
                  <Mui.TableCell onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        openMenu(e, c.id);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchorEl}
                      open={menuContactId === c.id}
                      onClose={closeMenu}
                    >
                      <MenuItem
                        onClick={() => {
                          history.push(`/admin/crm/edit/${c.id}`);
                          closeMenu();
                        }}
                      >
                        <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setDeleteModal({ isOpen: true, contact: c });
                          closeMenu();
                        }}
                        sx={{ color: "red" }}
                      >
                        <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                      </MenuItem>
                    </Menu>
                  </Mui.TableCell>
                </Mui.TableRow>
              ))
          )}
        </Mui.TableBody>
      </Mui.Table>

      {/* Pagination only if needed */}
      {data.length > rowsPerPage && (
        <Mui.TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-toolbar": {
              justifyContent: "flex-end",
              alignItems: "center",
            },
            ".MuiTablePagination-displayedRows": {
              marginLeft: "auto",
            },
          }}
        />
      )}
    </Mui.TableContainer>
  );
};
